/**
 * Setting the page: its size, its margins, how large the staves are and how far apart, and
 * how often the bars it holds are numbered.
 *
 * The whole layer is one door -- {@link set} takes any part of a layout and answers with
 * the command that writes it, or with the sentence saying why it will not. The checks are
 * about paper rather than taste: a staff outside the printed rastral sizes, a page smaller
 * than a postcard, margins that meet in the middle. Nothing here has an opinion about
 * whether 15mm is a handsome border.
 *
 * The page is one thing for the whole score. MusicXML can state a different size per page
 * and so can an engraver, but an editor whose page size depends on where the caret is
 * standing is an editor nobody can predict, and no music needs it.
 */

import { BAR_NUMBER_INTERVAL_MAX, PAGE_SIZES, STAFF_HEIGHT_RANGE } from "../document/layout";
import type { BarNumbers, Layout, PageMargins, PageSize } from "../document/layout";
import type { Score } from "../document/types";
import type { Command } from "../commands/types";

/** The least paper that can hold music once the margins are off it, in millimetres. */
const LEAST_PAGE = 50;
const MOST_PAGE = 1000;
const LEAST_ROOM = 20;

function measured(values: readonly number[]): boolean {
  return values.every((value) => Number.isFinite(value));
}

/**
 * Change any part of the page.
 *
 * The merged layout is what is checked, not the patch: margins are only too wide against
 * the page they sit on, and a page is only too narrow against the margins already set, so
 * a caller that moves one of them at a time has to be judged against the rest.
 *
 * The staff height is the exception, judged only when the patch sets it. It stands alone --
 * nothing else on the page is measured against it -- and an imported file may state a staff
 * outside the range, which import keeps as stated. Checking the stored one on every change
 * would lock such a score's margins, paper and bar numbers until its staff was resized first.
 */
export function set(score: Score, patch: Partial<Layout>): Command | string {
  const layout: Layout = { ...score.layout, ...patch };
  const { margins } = layout;

  if (
    !measured([
      layout.staffHeight,
      layout.pageWidth,
      layout.pageHeight,
      layout.systemGap,
      layout.staffGap,
      margins.left,
      margins.right,
      margins.top,
      margins.bottom,
    ])
  ) {
    return "a page is measured in numbers";
  }

  if (
    patch.staffHeight !== undefined &&
    (patch.staffHeight < STAFF_HEIGHT_RANGE.min || patch.staffHeight > STAFF_HEIGHT_RANGE.max)
  ) {
    return `a staff stands between ${STAFF_HEIGHT_RANGE.min} and ${STAFF_HEIGHT_RANGE.max} millimetres high`;
  }
  if (
    layout.pageWidth < LEAST_PAGE ||
    layout.pageHeight < LEAST_PAGE ||
    layout.pageWidth > MOST_PAGE ||
    layout.pageHeight > MOST_PAGE
  ) {
    return `a page is between ${LEAST_PAGE} and ${MOST_PAGE} millimetres each way`;
  }
  if (margins.left < 0 || margins.right < 0 || margins.top < 0 || margins.bottom < 0) {
    return "a margin is a border, and cannot be less than none";
  }
  if (
    layout.pageWidth - margins.left - margins.right < LEAST_ROOM ||
    layout.pageHeight - margins.top - margins.bottom < LEAST_ROOM
  ) {
    return "those margins leave no room for music";
  }
  if (layout.systemGap < 0 || layout.staffGap < 0) {
    return "a gap between staves is a distance, not a negative one";
  }
  if (typeof layout.barNumbers === "number") {
    const interval = layout.barNumbers;
    if (!Number.isInteger(interval) || interval < 1 || interval > BAR_NUMBER_INTERVAL_MAX) {
      return `bars are numbered every bar up to every ${BAR_NUMBER_INTERVAL_MAX}th, and a fraction of a bar is not a count`;
    }
  }

  return { kind: "setLayout", layout: patch };
}

/** Put the score on a named paper, keeping everything else. */
export function setPage(score: Score, size: PageSize): Command | string {
  return set(score, { pageWidth: size.width, pageHeight: size.height });
}

/** Turn the paper on its side, or back upright. */
export function setLandscape(score: Score, landscape: boolean): Command | string {
  const { pageWidth, pageHeight } = score.layout;
  const wide = pageWidth > pageHeight;
  if (wide === landscape) return "the page is already that way round";
  return set(score, { pageWidth: pageHeight, pageHeight: pageWidth });
}

/** The paper the score is on, when it is one with a name, held either way round. */
export function pageSizeOf(score: Score): PageSize | null {
  const { pageWidth, pageHeight } = score.layout;
  const near = (a: number, b: number): boolean => Math.abs(a - b) < 0.5;
  return (
    PAGE_SIZES.find(
      (size) =>
        (near(size.width, pageWidth) && near(size.height, pageHeight)) ||
        (near(size.height, pageWidth) && near(size.width, pageHeight)),
    ) ?? null
  );
}

/**
 * Gather runs of silent bars into one bar with a count over it, or stop gathering.
 *
 * On in a part and off in a score. {@link extractPart} turns it on for the part it
 * answers; this is the door for a user who wants it either way.
 */
export function setMultiRests(score: Score, on: boolean): Command | string {
  return set(score, { multiRests: on });
}

/**
 * Leave the staves out of a system where their part is silent, or draw them again.
 *
 * Off in a new score. A hidden staff cannot be clicked -- nothing was drawn to click --
 * so a caller that offers this should say so rather than leave the user hunting for a
 * part that has gone.
 */
export function setHideEmptyStaves(score: Score, on: boolean): Command | string {
  return set(score, { hideEmptyStaves: on });
}

/**
 * Number the bars: never, at the start of each system, or every so many bars.
 *
 * Each system is what a printed score does and what a new one is set to. A count is the
 * rehearsal score's way -- every bar, or every fifth -- and it is the distance between
 * printed numbers rather than the first number: a bar is numbered by where it stands in
 * the piece, so the pickup is still bar 0 and an inserted bar moves the rest along.
 */
export function setBarNumbers(score: Score, barNumbers: BarNumbers): Command | string {
  return set(score, { barNumbers });
}

/** Change one or more margins. */
export function setMargins(score: Score, margins: Partial<PageMargins>): Command | string {
  return set(score, { margins: { ...score.layout.margins, ...margins } });
}
